import * as React from "react"
import { NPKCatalogs } from "@smartdevis/server/src/domain"
import { H3, H4, H6, P } from "@smartdevis/ui/src/Typography"
import { FlexColumn, FlexRow, HorizontalSpace, VerticalSpace } from "@smartdevis/ui/src/utils/common"
import { i18n } from "../../../services/translations"
import { TypedOmit, F0, F1, Optionalize } from "@smartdevis/utils/src/types"
import { CatalogFormContainer } from "../devis-catalogs/devis-catalogs-styles"
import { NPKCatalogDrawing } from "./helper/crb-catalog-drawing"
import { Authentication } from "../../../store/authState"
import { formatNPKNumber, setABBNodes } from "./helper/utils"
import { CatalogPickerProps, CrbCatalogItem } from "./helper/crb-catalog-picker"
import { getChapterSection } from "./helper"
import { Spinner } from "@smartdevis/ui/src/Spinner"


const Placeholder = () => (
    <CatalogFormContainer direction="column" xAlign="center" yAlign="center" style={{ padding: "20px" }}>
        <H3>{i18n("Section overview")}</H3>
        <P textAlign="center">{i18n("Detailed overview of NPK section")}</P>
    </CatalogFormContainer>
)

type ConnectedCatalogFormProps<FormPayload, Item extends CrbCatalogItem> = TypedOmit<
    CatalogPickerProps<FormPayload, Item>,
    "visible" | "onClose" | "onSubmit" | "onRemove"
> & {
    catalogRef?: CrbCatalogItem | null
    catalogId?: string | undefined
    onBrowseCatalog: F0
    onCopy: F0
    isEditing: boolean
    noChanges?: boolean
    onUpdate: F1<Item>
    onAddToList: F1<Item>
    navigation: NPKCatalogs.ChapterRef
    setNavigation: F1<NPKCatalogs.ChapterRef>
    auth: Authentication
}

export const toggleNodeSelection = (node: NPKCatalogs.NPKNode, targetNumber: string): NPKCatalogs.NPKNode => {
    // Match the node and toggle isSelected
    if (node.number === targetNumber && node.isABB) {
        return {
            ...node,
            isSelected: !node.isSelected,
        };
    }

    // If it has subNodes, recursively apply this logic
    const updatedSubNodes = node.subNodes?.npknode.map(sub => toggleNodeSelection(sub, targetNumber)) || [];

    return {
        ...node,
        subNodes: {
            ...node.subNodes,
            npknode: updatedSubNodes,
        },
    };
};

export const NPKCatalogDetails = <FormPayload, Item extends CrbCatalogItem>(
    p: ConnectedCatalogFormProps<FormPayload, Item>
) => {
    if (!p.navigation.snumber) return <Placeholder />
    return <NPKCatalogItemForm {...p} />
}

type CatalogItemFormProps<FormPayload, Item extends CrbCatalogItem> = Optionalize<
    TypedOmit<ConnectedCatalogFormProps<FormPayload, Item>, "shelfVersion" | "catalogId" | "catalogRef">,
    "onBrowseCatalog"
> & { previewMode?: true; }
const NPKCatalogItemForm = <FormPayload, Item extends CrbCatalogItem>(p: CatalogItemFormProps<FormPayload, Item>) => {

    const [section, setSection] = React.useState<NPKCatalogs.NPKNode>();

    React.useEffect(() => {
        setSection(undefined);
        getChapterSection(p.auth, {
            chapter: p.navigation.number,
            language: p.navigation.language,
            year: p.navigation.version.toString(),
            sectionId: p.navigation.snumber
        }).then(res => {
            const section = res.value as NPKCatalogs.NPKNode
            setSection(setABBNodes(section));
        }).catch(err => err)
    }, [p.navigation.number, p.navigation.snumber])


    const recursizeSubNodesRender = (node1: NPKCatalogs.NPKNode) => (<>
        <>
            {node1.subNodes.npknode.map((node) => (
                <div style={{ backgroundColor: node.isSelected ? "yellow" : "" }}>
                    <FlexColumn>
                        <FlexRow spaceBetween>
                            <HorizontalSpace base={'10px'} />
                            <H6>{formatNPKNumber(node.number)}</H6>
                            <HorizontalSpace base={'10px'} />
                            <FlexColumn noShrink={false} style={{ width: "60%" }}>
                                <H6><u><b>{node.title}</b></u></H6>
                                <VerticalSpace base={'10px'} />
                                <H6>{node.description}</H6>
                                <VerticalSpace base={'10px'} />
                                {node.hint && <p style={{ color: "red" }}>* {node.hint}</p>}
                                <VerticalSpace base={'10px'} />
                                {node.drawingID && <NPKCatalogDrawing drawingId={node.drawingID} />}
                                <VerticalSpace base={'10px'} />
                            </FlexColumn>
                            <HorizontalSpace base={'10px'} />
                            <p style={{ width: "10%" }}>{node.isABB ? <input type="checkbox" checked={node.isSelected}
                                //  onClick={() => { p.onAddToList({ ...node, isSelected: !node.isSelected } as unknown as Item),
                                //  p.setNavigation({...p.navigation,wdNumber:node.number,workDescriptiom:node}) }}
                                onChange={() => {
                                    const updatedRoot = toggleNodeSelection(section!, node.number);
                                    setSection(updatedRoot);
                                    p.setNavigation({
                                        ...p.navigation,
                                        wdNumber: node.number,
                                        workDescriptiom: node,
                                    });
                                    p.onAddToList(p.navigation.workDescriptiom);
                                }}
                            /> : <></>}</p>
                        </FlexRow>
                        {node.variables && node.variables?.npkvariable!.length > 0 &&
                            <>
                                {node.variables?.npkvariable.map((variable) => (
                                    <FlexRow spaceAround>
                                        <HorizontalSpace base={'10px'} />
                                        <H6 style={{ width: "8.5rem", textAlign: "center" }}>{formatNPKNumber(variable.number)}</H6>
                                        <HorizontalSpace base={'10px'} />
                                        {/* <FlexColumn style={{ width: '60%' }}>
                                                {splitDescription(variable.description, variable.entryStart - 1)}
                                            </FlexColumn> */}
                                        <H6 style={{ width: '60%' }}>{variable.description}</H6>
                                        <HorizontalSpace base={'10px'} />
                                        <span style={{ width: "10%" }}>{variable.group === "A" ? <input type="radio" /> : <></>}</span>
                                        <HorizontalSpace base={'10px'} />
                                    </FlexRow>
                                ))}
                            </>}
                        <VerticalSpace base={'10px'} />
                        {!node.subNodes && node.unit &&
                            <ul>
                                <FlexRow spaceAround>
                                    <p style={{ width: "10%" }}></p>
                                    <div style={{ width: "60%" }}><input style={{ width: "-webkit-fill-available" }} type="number" disabled /></div>
                                    <HorizontalSpace base={'15px'} />
                                    <p style={{ width: "10%" }}>{node.unit}</p>
                                    <HorizontalSpace base={'15px'} />
                                </FlexRow>
                            </ul>}
                        {node.subNodes && node.subNodes.npknode.length > 0 && recursizeSubNodesRender(node)}
                    </FlexColumn>
                </div>
            ))}
        </>
    </>)

    if (!section) return <Spinner />
    return (
        <CatalogFormContainer overflow="scroll" direction="column">
            {section ? <>
                {section.subNodes?.npknode.length > 0 &&
                    <>
                        <FlexColumn>
                            <FlexRow>
                                <HorizontalSpace base={'10px'} />
                                <H4>{formatNPKNumber(section.number)}</H4>
                                <HorizontalSpace base={'10px'} />
                                <FlexColumn noShrink={false} style={{ width: "60%" }}>
                                    <H4>{section.title}</H4>
                                    <VerticalSpace base={'10px'} />
                                    <H6>{section.description}</H6>
                                    <VerticalSpace base={'10px'} />
                                    {section.hint && <p>* {section.hint}</p>}
                                    <VerticalSpace base={'10px'} />
                                </FlexColumn>
                            </FlexRow>
                            {recursizeSubNodesRender(section)}
                        </FlexColumn>
                    </>
                }
            </> : <></>}
        </CatalogFormContainer>
    )
}
